import { memo, useCallback } from 'react';

import { Form } from 'src/shared/components/form/form.js';
import { MessageToast } from 'src/shared/components/message-toast/message-toast.js';
import { OpenGmailButton } from 'src/shared/components/open-gmail-button/open-gmail-button.js';
import { useShowMailSentToast } from 'src/shared/hooks/use-mail-sent.js';
import { useShowGmailButton } from 'src/shared/hooks/use-show-gmail-button.js';
import type { KcContext_MagicLinkForm } from 'src/shared/utils/kc-context/kc-context.js';
import { useGetMsg } from 'src/shared/utils/kc-messages.js';
import { redirectToSameUrlWithoutTab } from 'src/shared/utils/redirect-to-same-url-without-tab.js';

import { cnMagicLinkForm } from './magic-link-form.constants.js';

import './magic-link-form.css';

export interface MagicLinkFormProps {
    kcContext: KcContext_MagicLinkForm;
}

export const MagicLinkForm = memo<MagicLinkFormProps>(({ kcContext }) => {
    const { showMailSentToast, setShowMailSentToast, setWasMailSent } = useShowMailSentToast();
    const { url, auth } = kcContext;
    const showGmailButton = useShowGmailButton(auth.attemptedUsername || '');
    const { msgStr } = useGetMsg(kcContext);

    const handleResendClick = useCallback(() => {
        setWasMailSent('true');
    }, [setWasMailSent]);

    return (
        <Form action={url.loginAction} kcContext={kcContext} className={cnMagicLinkForm()}>
            {showMailSentToast && (
                <MessageToast message={msgStr('emailResent')} onClose={() => setShowMailSentToast(false)} />
            )}

            <div className={cnMagicLinkForm('text-block')}>
                <span className={cnMagicLinkForm('title')}>{msgStr('emailSentMessageStart')} </span>
                <span className={cnMagicLinkForm('title', { email: true })}>
                    {auth.attemptedUsername || msgStr('yourEmail')}
                </span>
                <span className={cnMagicLinkForm('title')}> {msgStr('emailSentMessageLogIn')}</span>
            </div>

            {showGmailButton && (
                <div className={cnMagicLinkForm('text-block')}>
                    <OpenGmailButton message={msgStr('openGmail')} />
                </div>
            )}

            <div className={cnMagicLinkForm('text-block')}>
                <span className={cnMagicLinkForm('note')}>{msgStr('emailVerifyInstruction2') + ' '}</span>
                <button
                    className={cnMagicLinkForm('action-text')}
                    type="submit"
                    value="resendMagicLink"
                    name="submitAction"
                    onClick={handleResendClick}
                >
                    <span className={cnMagicLinkForm('note', { accent: true })}>{msgStr('magicLinkResend')}</span>
                </button>
            </div>

            <div
                className={cnMagicLinkForm('text-block', {
                    'without-margin': true,
                })}
            >
                <span
                    onClick={redirectToSameUrlWithoutTab}
                    className={cnMagicLinkForm('note', {
                        accent: true,
                    })}
                >
                    {msgStr('emailVerifyInstructionCancel')}
                </span>
            </div>
        </Form>
    );
});
